import { Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useOwnerIdentity } from "@/lib/useOwnerIdentity";
import { useTenantIdentity } from "@/lib/useTenantIdentity";
import { useUserOrganizations } from "@/lib/org";
import { LoadingScreen } from "@/components/shell/LoadingScreen";

// Phase 6.12.2 - Onboarding guard.
// /onboarding previously rendered the org-creation form for ANY
// authenticated user. A linked owner or tenant who reached it directly
// (bookmark, stale redirect, typed URL) was offered a form that would have
// made them a manager and permanently captured them in RoleHome's
// manager-first precedence. This guard redirects linked owners and tenants
// to their portals and lets everyone else through, including managers
// creating an additional organization via the org switcher.
//
// Same identity sources and hold-until-settled pattern as RoleHome and
// OwnerRoute: no routing decision is made on unsettled queries.

export function OnboardingRoute({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const {
    data: memberships,
    isSuccess: orgsLoaded,
  } = useUserOrganizations();
  const {
    data: owner,
    isSuccess: ownerLoaded,
  } = useOwnerIdentity();
  const {
    data: tenant,
    isSuccess: tenantLoaded,
  } = useTenantIdentity();

  if (authLoading) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (!orgsLoaded || !ownerLoaded || !tenantLoaded) return <LoadingScreen />;

  const isManager = (memberships?.length ?? 0) > 0;
  if (!isManager && owner) return <Navigate to="/owner" replace />;
  if (!isManager && tenant) return <Navigate to="/tenant" replace />;

  return <>{children}</>;
}
